import type { KaizenOverride } from '../context/SimulationContext';

export interface OptimizationNodeInput {
  id: string;
  name: string;
  traMinutes: number;
  tpMinutes?: number;
  baseOperators: number;
  hourlyCost?: number;
}

export interface OptimizeWorkflowInput {
  nodes: OptimizationNodeInput[];
  demand: number;
  shiftMinutes: number;
  batchSize?: number;
  horizonDays?: number;
  ticketAverage?: number;
  defaultHourlyCost?: number;
  currentOverrides?: Record<string, KaizenOverride>;
  maxIterations?: number;
  waitReductionStepPercent?: number;
  targetMinLoadPercent?: number;
  targetMaxLoadPercent?: number;
}

export interface OptimizationNodeStatus {
  id: string;
  name: string;
  loadPercent: number;
  overflowPerDay: number;
  extraFte: number;
  waitReductionPercent: number;
}

export interface OptimizeWorkflowResult {
  isBalanced: boolean;
  iterations: number;
  executionMs: number;
  optimizedOverrides: Record<string, KaizenOverride>;
  bottleneckNodeId: string | null;
  leadTimeBeforeDays: number;
  leadTimeAfterDays: number;
  leadTimeReductionPercent: number;
  costBefore: number;
  costAfter: number;
  addedInvestment: number;
  nodeStatus: OptimizationNodeStatus[];
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const toFinite = (value: unknown, fallback = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeOverride = (override?: KaizenOverride): Required<Pick<KaizenOverride, 'fte' | 'rework'>> => {
  const fte = toFinite(override?.fte ?? override?.extraFTE, 0);
  const rework = toFinite(override?.rework ?? override?.reworkReduction, 0);
  return {
    fte: clamp(fte, 0, 99),
    rework: clamp(rework, 0, 95),
  };
};

interface NodeEvaluation {
  id: string;
  name: string;
  loadPercent: number;
  overflowPerDay: number;
  flowDays: number;
  extraFte: number;
  waitReductionPercent: number;
}

const evaluateNode = (
  node: OptimizationNodeInput,
  override: Required<Pick<KaizenOverride, 'fte' | 'rework'>>,
  demand: number,
  shiftMinutes: number,
): NodeEvaluation => {
  const traMinutes = Math.max(0.01, toFinite(node.traMinutes, 0.01));
  const baseOperators = Math.max(1, Math.round(toFinite(node.baseOperators, 1)));
  const extraFte = Math.max(0, Math.round(override.fte));
  const waitReductionPercent = clamp(toFinite(override.rework, 0), 0, 95);

  // Kaizen de fluxo: modelado como redução de tempo improdutivo na etapa.
  const adjustedTraMinutes = traMinutes * (1 - waitReductionPercent / 100);
  const effectiveOperators = baseOperators + extraFte;
  const capacityPerDay = adjustedTraMinutes > 0 ? (shiftMinutes * effectiveOperators) / adjustedTraMinutes : 0;
  const loadPercent = capacityPerDay > 0 ? (demand / capacityPerDay) * 100 : 999;
  const overflowPerDay = Math.max(0, Math.round(demand - capacityPerDay));
  const flowDays = capacityPerDay > 0 ? demand / capacityPerDay / (shiftMinutes / 60) : 1;

  return {
    id: node.id,
    name: node.name,
    loadPercent,
    overflowPerDay,
    flowDays,
    extraFte,
    waitReductionPercent,
  };
};

const evaluateTotals = (
  nodes: OptimizationNodeInput[],
  overrides: Record<string, Required<Pick<KaizenOverride, 'fte' | 'rework'>>>,
  demand: number,
  shiftMinutes: number,
  horizonDays: number,
  batchSize: number,
  ticketAverage: number,
  targetMinLoad: number,
  targetMaxLoad: number,
) => {
  const evaluations = nodes.map((node) =>
    evaluateNode(node, overrides[node.id] ?? { fte: 0, rework: 0 }, demand, shiftMinutes),
  );

  const totalQueue = evaluations.reduce((sum, item) => sum + Math.max(0, item.overflowPerDay) * horizonDays, 0);
  const leadTimeDays = evaluations.reduce((sum, item) => sum + item.flowDays, 0) * Math.max(1, batchSize / 10);
  const inertiaCost = totalQueue * ticketAverage;

  return {
    evaluations,
    totalQueue,
    leadTimeDays,
    inertiaCost,
    isBalanced: evaluations.every((item) => {
      if (item.loadPercent > targetMaxLoad) return false;
      if (item.loadPercent < targetMinLoad) {
        const applied = overrides[item.id] ?? { fte: 0, rework: 0 };
        return Math.round(applied.fte) <= 0;
      }
      return true;
    }),
    bottleneck: evaluations.reduce<NodeEvaluation | null>((worst, current) => {
      if (!worst || current.loadPercent > worst.loadPercent) return current;
      return worst;
    }, null),
  };
};

export function optimizeWorkflow(currentData: OptimizeWorkflowInput): OptimizeWorkflowResult {
  const startTime =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();

  const demand = Math.max(1, Math.round(toFinite(currentData.demand, 1)));
  const shiftMinutes = Math.max(60, Math.round(toFinite(currentData.shiftMinutes, 480)));
  const horizonDays = Math.max(1, Math.round(toFinite(currentData.horizonDays, 22)));
  const batchSize = Math.max(1, Math.round(toFinite(currentData.batchSize, 1)));
  const ticketAverage = Math.max(0, toFinite(currentData.ticketAverage, 1800));
  const defaultHourlyCost = Math.max(0, toFinite(currentData.defaultHourlyCost, 85));
  const targetMinLoad = clamp(toFinite(currentData.targetMinLoadPercent, 85), 1, 200);
  const targetMaxLoad = clamp(toFinite(currentData.targetMaxLoadPercent, 95), targetMinLoad, 300);
  const nodes = (currentData.nodes ?? []).filter((node) => toFinite(node.traMinutes, 0) > 0);
  const maxIterations = Math.max(1, Math.min(500, Math.round(toFinite(currentData.maxIterations, nodes.length * 16 || 80))));

  const normalizedOverrides: Record<string, Required<Pick<KaizenOverride, 'fte' | 'rework'>>> = {};
  nodes.forEach((node) => {
    normalizedOverrides[node.id] = normalizeOverride(currentData.currentOverrides?.[node.id]);
  });

  const baseline = evaluateTotals(
    nodes,
    normalizedOverrides,
    demand,
    shiftMinutes,
    horizonDays,
    batchSize,
    ticketAverage,
    targetMinLoad,
    targetMaxLoad,
  );

  let iterations = 0;
  let runningOverrides: Record<string, Required<Pick<KaizenOverride, 'fte' | 'rework'>>> = {
    ...normalizedOverrides,
  };
  let latest = baseline;

  while (!latest.isBalanced && iterations < maxIterations) {
    const candidate = latest.evaluations
      .map((item) => {
        const current = runningOverrides[item.id] ?? { fte: 0, rework: 0 };
        const canDecrease = Math.round(current.fte) > 0;
        const isOver = item.loadPercent > targetMaxLoad;
        const isUnder = item.loadPercent < targetMinLoad && canDecrease;
        const direction: 'up' | 'down' | 'none' = isOver ? 'up' : isUnder ? 'down' : 'none';
        const deviation = isOver
          ? item.loadPercent - targetMaxLoad
          : isUnder
            ? targetMinLoad - item.loadPercent
            : 0;
        return { item, current, direction, deviation };
      })
      .filter((entry) => entry.direction !== 'none' && entry.deviation > 0)
      .sort((a, b) => b.deviation - a.deviation)[0];

    if (!candidate) break;

    const current = candidate.current;
    runningOverrides = {
      ...runningOverrides,
      [candidate.item.id]: {
        // Auto-balanceamento focado em FTE para manter ocupação entre 85-95%.
        fte:
          candidate.direction === 'up'
            ? clamp(Math.round(current.fte) + 1, 0, 99)
            : clamp(Math.round(current.fte) - 1, 0, 99),
        // Mantém ajuste de fluxo já aplicado; sem incremento automático nesta rotina.
        rework: clamp(current.rework, 0, 95),
      },
    };

    latest = evaluateTotals(
      nodes,
      runningOverrides,
      demand,
      shiftMinutes,
      horizonDays,
      batchSize,
      ticketAverage,
      targetMinLoad,
      targetMaxLoad,
    );
    iterations += 1;
  }

  const addedInvestment = nodes.reduce((sum, node) => {
    const before = normalizedOverrides[node.id] ?? { fte: 0, rework: 0 };
    const after = runningOverrides[node.id] ?? before;
    const addedFte = Math.max(0, Math.round(after.fte) - Math.round(before.fte));
    if (addedFte <= 0) return sum;
    const hourlyCost = Math.max(0, toFinite(node.hourlyCost, defaultHourlyCost));
    const periodHours = (shiftMinutes / 60) * horizonDays;
    return sum + addedFte * hourlyCost * periodHours;
  }, 0);

  const leadTimeReductionPercent =
    baseline.leadTimeDays > 0
      ? ((baseline.leadTimeDays - latest.leadTimeDays) / baseline.leadTimeDays) * 100
      : 0;

  const endTime =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();

  const optimizedOverrides: Record<string, KaizenOverride> = {};
  Object.entries(runningOverrides).forEach(([nodeId, value]) => {
    optimizedOverrides[nodeId] = {
      fte: value.fte,
      rework: value.rework,
      extraFTE: value.fte,
      reworkReduction: value.rework,
    };
  });

  return {
    isBalanced: latest.isBalanced,
    iterations,
    executionMs: Math.max(0, endTime - startTime),
    optimizedOverrides,
    bottleneckNodeId: latest.bottleneck?.id ?? null,
    leadTimeBeforeDays: baseline.leadTimeDays,
    leadTimeAfterDays: latest.leadTimeDays,
    leadTimeReductionPercent: leadTimeReductionPercent > 0 ? leadTimeReductionPercent : 0,
    costBefore: baseline.inertiaCost,
    costAfter: latest.inertiaCost,
    addedInvestment,
    nodeStatus: latest.evaluations.map((item) => ({
      id: item.id,
      name: item.name,
      loadPercent: item.loadPercent,
      overflowPerDay: item.overflowPerDay,
      extraFte: item.extraFte,
      waitReductionPercent: item.waitReductionPercent,
    })),
  };
}
